import type { UpdateShippingFeeSettingsInput } from '@repo/api-contracts';
import { useState } from 'react';
import { useInvalidateShippingFeeSettings } from '@/entities/shipping-fee';
import { trpc } from '@/shared/api';
import { getErrorMessage } from '@/shared/lib';

export interface UseUpdateShippingFeeSettingsResult {
  /** 성공하면(설정을 다시 읽은 뒤) true. 서버가 거부하면 false 이고 문구는 error 에 남는다. */
  readonly update: (input: UpdateShippingFeeSettingsInput) => Promise<boolean>;
  /** 요청과 그 뒤 설정 재조회가 끝날 때까지 true — 바뀐 값이 오기 전에 다시 저장하지 못하게 한다. */
  readonly isPending: boolean;
  readonly error: string | null;
}

const FORM_ERROR_FALLBACK = '배송비 설정을 저장하지 못했습니다.';

/**
 * 배송비 설정 저장(shippingFeeSettings.update, SUPER-86 D9) — 다음 견적 요청과 새로 채우는 배송비부터 적용되고 이미 만든 초안·발행본에는
 * 소급하지 않는다. 감사 SHIPPING_FEE.SETTINGS_UPDATED 는 서버가 남긴다. 성공하면 설정을 다시 읽을 때까지 진행 중으로 남는다.
 */
export const useUpdateShippingFeeSettings = (): UseUpdateShippingFeeSettingsResult => {
  const [error, setError] = useState<string | null>(null);
  const settings = useInvalidateShippingFeeSettings();
  const mutation = trpc.shippingFeeSettings.update.useMutation({
    // 재조회 promise 를 돌려준다 — 라이브러리가 그것을 기다려, 새 값이 올 때까지 isPending 이 남는다.
    onSuccess: () => settings.invalidate(),
  });

  const update = async (input: UpdateShippingFeeSettingsInput): Promise<boolean> => {
    setError(null);
    try {
      await mutation.mutateAsync(input);
      return true;
    } catch (cause) {
      setError(getErrorMessage(cause, FORM_ERROR_FALLBACK));
      return false;
    }
  };

  return { update, isPending: mutation.isPending, error };
};
